import React from 'react';

import {Box} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import {useHistory} from 'react-router-dom';

import {
    ATTR_NULL,
    Constants,
    HistoryRoutes,
    Messages,
    SnackbarVariants,
    ViewKeys,
} from 'commons/constants';
import {ClientService} from 'services';
import {EfeAccordion} from 'components';
import {MainContent} from 'layouts/commons';
import {nextStep} from 'commons/utils/Functions';
import {fieldCatalogGetter} from 'hooks/FlowHook';
import BeneficiaryAddressForm from './form/AddressForm';
import {useAlert, useFlowFieldSearch, useSpinner} from 'hooks';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import {useBeneficiaryContext} from 'context/beneficiary-context/BeneficiaryContext';
import {AddressServiceRequest, EfeAccordionValues, EfeResponse, Field, View} from 'commons/models';

function BeneficiaryAddress() {
    const history = useHistory();
    const {flow, updateModuleStep} = useFlowContext();
    const [viewFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const [fields, setFields] = React.useState<Array<Field>>([]);
    const {menu, expedientId, fatherKycId} = useMenuContext();
    const {tempBeneficiary, setTempBeneficiary, setActive} = useBeneficiaryContext();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const {enqueueSnackbar} = useSnackbar();
    const [auxBeneficiary, setAuxRealOwnerInfo] = React.useState<any>();
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();

    async function onSubmit(values: AddressServiceRequest) {
        setIsLoading(true);
        const request: AddressServiceRequest = {
            ...values,
            kycId: tempBeneficiary?.kycId,
            viewId: flow.data.viewId,
            viewFatherId: flow.data.fatherId,
            expedientId: expedientId,
        };
        try {
            const response: EfeResponse<AddressServiceRequest> =
                await ClientService.saveBeneficiaryAddress(request);
            if (response && response.data) {
                enqueueSnackbar(Messages.INFORMATION_SAVED_SUCCESSFULLY, {
                    variant: SnackbarVariants.SUCCESS,
                });
                setFields([...fields]);
                const path = nextStep(flow, menu);
                if (tempBeneficiary) {
                    setActive(false);
                    setTempBeneficiary(undefined);
                }
                if (Constants.MODULE_STEP !== path) {
                    setIsLoading(false);
                    history.push(path);
                } else {
                    updateModuleStep(true);
                    flow.data.expandKey = path;
                    setIsLoading(false);
                }
            } else {
                setFields([...fields]);
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
            setFields([...fields]);
            showAlertError(error, Messages.ERROR_SAVING_ADDRESS);
        }
    }

    function overrideFields(view: View | undefined) {
        if (view && view.fields) {
            setFields(view.fields);
        } else {
            setFields(viewFields);
        }
    }

    React.useEffect(() => {
        if (!fields || (fields && fields.length <= 0)) {
            setFields(viewFields);
        }
        if (tempBeneficiary && fatherKycId > 0 && tempBeneficiary.kycId > 0) {
            (async function () {
                try {
                    setIsLoading(true);
                    const response: EfeResponse<AddressServiceRequest> =
                        await ClientService.getBeneficiaryAddress(tempBeneficiary?.kycId);
                    if (response && response.data) {
                        setAuxRealOwnerInfo(response.data);
                    }
                    setIsLoading(false);
                } catch (error) {
                    setIsLoading(false);
                    setAuxRealOwnerInfo(undefined);
                }
            })();
        }
    }, []);

    const accordion: Array<EfeAccordionValues> = [
        {
            icon: <ActionIcon />,
            title: 'Direcci\u00F3n del Beneficiario',
            reactElement: (
                <Box mt={8}>
                    {!isLoading && (
                        <BeneficiaryAddressForm
                            viewId={flow.data.viewId}
                            submitHandler={onSubmit}
                            viewKey={flow.data.id}
                            foreignCitizen={tempBeneficiary?.foreignCitizen || false}
                            fieldHandler={overrideFields}
                            auxBeneficiary={auxBeneficiary}
                            fields={[
                                ...fieldCatalogGetter(
                                    fields.filter((field) => ATTR_NULL !== field.attrName)
                                ),
                            ].sort((a: Field, b: Field) => (a.idOrder < b.idOrder ? -1 : 1))}
                        />
                    )}
                </Box>
            ),
            expandIcon: true,
            disabled: false,
            expanded: ViewKeys.BENEFICIARY_ADDRESS === flow.data.expandKey,
        },
    ];

    return (
        <MainContent>
            {useSpinner(isLoading)}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}

            <EfeAccordion
                onChange={() => history.push(HistoryRoutes.BENEFICIARY_ADDRESS)}
                values={accordion}
            />
        </MainContent>
    );
}

export default BeneficiaryAddress;
